import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpResponseBase
} from '@angular/common/http';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { sm4 } from 'sm-crypto';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { decrypt } from '@shared/util/sm2';
import { NzModalService } from 'ng-zorro-antd/modal';
import { EMPTY, Observable, Subject, of, throwError } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { BasicConfigService } from '@core/service/basicConfig.service';
import { catchError, filter, mergeMap, takeUntil } from 'rxjs/operators';
import { resetUser, setLoginMethod } from '../store';
import { I18nMessageService } from './../service/i18nMessage.service';
import { checkStatus, keyMap } from './interceptor.type';

/**
 * HTTP Response拦截器
 */
@Injectable()
export class ResponseInterceptor implements HttpInterceptor, OnDestroy {
  private get store(): Store {
    return this.injector.get(Store);
  }

  private get router(): Router {
    return this.injector.get(Router);
  }

  private get messageService(): I18nMessageService {
    return this.injector.get(I18nMessageService);
  }

  private get notifyService(): NzNotificationService {
    return this.injector.get(NzNotificationService);
  }

  private get modalService(): NzModalService {
    return this.injector.get(NzModalService);
  }

  private get configService(): BasicConfigService {
    return this.injector.get(BasicConfigService);
  }

  private showInfo = false;

  private destroySub$: Subject<void> = new Subject<void>();

  constructor(private injector: Injector) {}

  ngOnDestroy(): void {
    this.destroySub$.next();
    this.destroySub$.complete();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      mergeMap(ev => {
        if (!(ev instanceof HttpResponseBase) || !(ev instanceof HttpResponse))
          return ev instanceof HttpErrorResponse ? throwError(ev) : of(ev);
        // 是否需要加密
        const isGlobalEncrypt: boolean = this.configService.get('isGlobalEncrypt') || false;
        // 解密响应报文
        const needEncrypt: boolean = req.headers.get('Xencio-need-decrypt') === 'true';
        const isFileDownload: boolean = ['arraybuffer', 'blob'].includes(req.responseType.toLocaleLowerCase());

        if (typeof isGlobalEncrypt === 'boolean' && isGlobalEncrypt && needEncrypt && !isFileDownload) {
          const loginEnv: string = this.configService.get('loginEnv') || 'other';
          const clientPrivateKey: string = keyMap[loginEnv].pri;

          const Xencio_key = ev.headers.get('Xencio-key');
          const decryptKey = decrypt(Xencio_key, clientPrivateKey);
          if (!decryptKey) {
            this.notifyService.error('请求&响应加解密', '接口返回解密失败', { nzDuration: 0 });
          }
          const res = JSON.parse(sm4.decrypt((ev as any).body, decryptKey));
          Object.assign(ev, { body: res });
        }

        // 解密报文后，若一切都正常，则后续操作
        const body = ev.body;
        if (
          ev.url.endsWith('.json') ||
          body instanceof Blob ||
          body instanceof ArrayBuffer ||
          ev.url.endsWith('/datamaintain/rollingforecast/ajaxUpload')
        ) {
          return of(ev);
        }

        if (body.code !== '200' && !this.checkSpecialFormat(ev)) {
          if (body.code === '401' || body.code === '40101') {
            this.toLogin(req, body);
            // } else if (body.code === '40301') {
            //   this.refreshPageForChange();
          } else {
            console.error(`请求错误 ${ev.status}: ${ev.url} ${body}`);
          }
          return EMPTY;
        } else {
          // 重新修改 `body` 内容为 `response` 内容，对于绝大多数场景已经无须再关心业务状态码
          const newBody: any = { body: body.data ?? {} };
          if (body.message && (!body.data || !Array.isArray(body.data))) {
            let language = localStorage.NG_TRANSLATE_LANG_KEY === 'en' ? 'en' : 'zh';
            newBody.body.message = body.message;
            const i18nMsg = this.messageService.i18nMessage[body.message];
            newBody.body.msg = i18nMsg
              ? i18nMsg[language] || ''
              : this.isValidJSON(body.message)
              ? JSON.parse(body.message)[language] || ''
              : body.message;
          }
          return of(new HttpResponse(Object.assign(ev, newBody)));
        }
      }),
      catchError((err: HttpErrorResponse) => {
        checkStatus(err);
        switch (err.status) {
          case 0:
            this.refreshPage();
            break;
          case 401:
            this.toLogin(req);
            break;
          case 500:
            this.modalService.closeAll();
            this.router.navigateByUrl(`/exception/${err.status}`);
            break;
          default:
            console.error('未知错误，大部分是由于网络问题引起，请联系管理员处理', err);
        }
        return throwError(err);
      })
    );
  }

  private refreshPage() {
    if (!this.showInfo) {
      this.showInfo = true;
      setTimeout(() => {
        this.showInfo = false;
      }, 30000);
      this.modalService.info({
        nzTitle: $localize`提示`,
        nzContent: $localize`检测到网络异常，点击确定后将刷新页面`,
        nzClosable: false,
        nzOnOk: () => {
          window.location.reload();
        }
      });
    }
  }

  private refreshPageForChange() {
    const url: string = window.location.href;
    if (url && url.includes('/funding-board')) return;
    if (!this.showInfo) {
      this.showInfo = true;
      setTimeout(() => {
        this.showInfo = false;
      }, 30000);
      this.modalService.info({
        nzTitle: $localize`提示`,
        nzContent: $localize`检测到页面有变动，将为您自动刷新`,
        nzClosable: false,
        nzOnOk: () => {
          window.location.reload();
        }
      });
    }
  }

  private toLogin(req: HttpRequest<any>, res?: any): void {
    // window.alert(`未登录或登录已过期，请重新登录。`);
    if (req.url.includes('/account/ping')) {
      if (res?.data?.loginMethod) {
        this.store.dispatch(setLoginMethod({ loginMethod: res.data.loginMethod }));
      }
      this.store.dispatch(resetUser());
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.slice(hash.indexOf('?') + 1));
      if (
        !(
          (hash.includes('passport/oalogin') && params.get('token')) ||
          hash.includes('passport/ssologin') ||
          hash.includes('exception/forbidden')
        )
      )
        this.router.navigate(['passport', 'expired'], { queryParams: { tips: 'invalid' } });
    } else {
      if (!this.showInfo) {
        this.showInfo = true;
        setTimeout(() => {
          this.showInfo = false;
        }, 30000);
        this.modalService.info({
          nzTitle: $localize`提示`,
          nzContent: $localize`长时间未进行操作，请重新登录系统。`,
          nzClosable: false,
          nzOnOk: () => {
            this.store.dispatch(resetUser());
            this.router.navigate(['passport', 'expired'], {
              queryParams: { reloadUrl: this.router.routerState.snapshot.url, tips: 'invalid' }
            });
          }
        });
      }
    }
  }

  private checkSpecialFormat(ev: HttpResponse<any>): boolean {
    return [
      '/personal/sendMobileVerficationCode',
      '/personal/bindMobilePhone',
      '/account/loginByVerificationCode',
      '/datamaintain/budget/uploadTemplate'
    ].some(url => ev.url.includes(url));
  }

  private isValidJSON(str: string) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }
}
